;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import { useEffect, useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { addBreadcrumb, logSentryError } from '../utils/logSentry';
import axiosInstance from '../utils/axios';

// Prod: Load VAPID public key from environment variable
let { VAPID_PUBKEY } = process.env;
if (!VAPID_PUBKEY) {
  // Dev: Load VAPID public key from file
  try {
    // eslint-disable-next-line
    const keyfile = require('../VAPID.json');
    VAPID_PUBKEY = keyfile.publicKey;
  } catch (e) {
    console.error('Notification Init failed: Could not load VAPID public key');
    logSentryError(e);
  }
}

interface NotificationsHook {
  askPermission: () => Promise<void>;
  hasPermission: () => boolean;
  setTopics: (topics: string[]) => Promise<void>;
  getTopics: () => Promise<string[]>;
}

function useNotifications(): NotificationsHook {
  const userContext = useContext(UserContext);
  const isUserSignedIn = !!userContext?.user;

  // iOS Safari (buiten een geïnstalleerde PWA) en oudere browsers kennen de Notification API niet
  const isSupported = () => typeof window !== 'undefined' && 'Notification' in window;
  const hasPermission = () => isSupported() && Notification.permission === 'granted';

  /**
   * Configure the types of notifications the user wants to receive.
   */
  const setTopics = async (topics: string[]): Promise<void> => {
    if (!userContext?.user) throw new Error('User must be signed in to set topics');
    await axiosInstance.patch('/notifications/topics', { topics });
  };
  const getTopics = async (): Promise<string[]> => {
    if (!userContext?.user) throw new Error('User must be signed in to get topics');
    const response = await axiosInstance.get('/notifications/topics');
    return response.data.topics;
  };

  /**
   * Notifications get sent through a subscription with the browser's message broker.
   * This function gets an existing subscription, or creates a new one if none exists.
   * @returns PushSubscription object, or null if no subscription could be created.
   */
  const getSubscription = async (): Promise<PushSubscription | null> => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) return subscription;
      return await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: VAPID_PUBKEY,
      });
    } catch (error) {
      // Unless the user explicitly denied notifications, we screwed up: Tell Sentry.
      const blockedByUser = isSupported() && Notification.permission === 'denied';
      if (!blockedByUser) {
        addBreadcrumb({
          category: 'notifications',
          message: 'Error while getting push subscription',
          level: 'error',
          data: { error },
        });
      }
      return null;
    }
  };

  /**
   * Ensures our backend knows about this browser's subscription.
   * Internal function: Only call from inside useNotifications hook.
   */
  async function syncSubscription(): Promise<void> {
    if (!isUserSignedIn || !hasPermission()) return;
    const subscription = await getSubscription();
    if (!subscription) return;
    await axiosInstance.post('/notifications/subscriptions', subscription);
  }

  /**
   * Displays a popup asking the user to permit notifications.
   * If the user accepts, a subscription will be created and sent to the backend.
   */
  const askPermission = async (): Promise<void> => {
    if (!isSupported()) console.warn('This browser does not support desktop notifications');
    else {
      try {
        const result = await Notification.requestPermission();
        if (result !== 'granted') throw new Error('Permission denied');
        await syncSubscription();
      } catch (error) {
        if (error.message !== 'Permission denied') {
          // Unless the user explicitly denied notifications, we screwed up: Tell Sentry.
          addBreadcrumb({
            category: 'notifications',
            message: 'Error while asking for permission',
            level: 'error',
          });
          logSentryError(error);
        }
        return Promise.reject(error);
      }
    }
  };

  useEffect(() => {
    syncSubscription();
  }, [isUserSignedIn]);

  return { askPermission, hasPermission, setTopics, getTopics };
}

export default useNotifications;
